import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldAlert, Play, Loader2, RefreshCw, Clock, CheckCircle2,
  XCircle, AlertTriangle, FileSearch, Lock, Syringe, FileSignature, ArrowLeft,
  Wrench, Lightbulb,
} from "lucide-react";
import { diagnoseRun, explainFailure, TONE_STYLES } from "@/utils/agentTroubleshoot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  running: "bg-blue-50 text-blue-700 border-blue-200",
  queued: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

// Per-agent copy & behaviour. The backend endpoints are shared (agent_type param).
const AGENT_META = {
  vaccine_compliance: {
    title: "Vaccine Compliance Agent",
    short: "Vaccine Compliance",
    Icon: Syringe,
    description: "Automatically send Gingr-native vaccine reminders for upcoming reservations with expired or missing vaccine records.",
    reportName: "Expired Vaccinations report",
    liveNoun: "Gingr Email Reminders",
    primaryKey: "red_shields",
    primaryLabel: "Red shields",
    // In LIVE mode we ONLY count truly-sent customers under the "Reminders"
    // header. Detected-but-failed customers appear ONLY in the "Couldn't send"
    // section below, so staff never see the same name in both lists.
    detectEventsDry: ["red_shield_detected_dry_run"],
    detectEventsLive: ["send_confirmed"],
    skipEvent: "red_shield_skipped",
    detectNounDry: "Would remind",
    detectNounLive: "Reminders",
    showAgreementType: false,
    steps: ["Native Gingr Email Reminder", "Same-day duplicates skipped", "Full audit log + screenshots"],
    liveBody: (n) => (<>This will send <b>real</b> Gingr Email Reminders to up to <b>{n}</b> customer(s) with upcoming reservations and missing/expired vaccine records. Already-reminded customers (within your dedup window) are skipped.</>),
  },
  unsigned_agreements: {
    title: "Unsigned Agreements Agent",
    short: "Unsigned Agreements",
    Icon: FileSignature,
    description: "Automatically push Gingr agreements to the customer's device and text the signature link for upcoming reservations with unsigned policies.",
    reportName: "Unsigned Agreements report",
    liveNoun: "Gingr agreement-signature SMS messages",
    primaryKey: "customers_detected",
    primaryLabel: "Customers",
    detectEventsDry: ["customer_detected_dry_run"],
    detectEventsLive: ["send_confirmed"],
    skipEvent: "customer_skipped",
    detectNounDry: "Would send to",
    detectNounLive: "Sent to",
    showAgreementType: true,
    steps: ["Native Gingr Push Agreements → Send SMS", "Only sends when Gingr has a phone on file", "Same-day duplicates skipped", "Full audit log + screenshots"],
    liveBody: (n) => (<>This will send <b>real</b> Gingr agreement-signature SMS messages to up to <b>{n}</b> customer(s) with upcoming reservations and unsigned agreements. Customers already texted (within your dedup window) are skipped. Gingr must have a phone number on file.</>),
  },
};

const DEFAULT_FORM = {
  app_url: "", email: "", password: "", location_name: "", gingr_location_id: "",
  agreement_type: "Policies",
  lookahead_days: 30, duplicate_window_days: 1, max_sends_per_run: 10,
  schedule_time: "18:00", timezone: "America/New_York", skip_row_text: "",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function to12(hhmm) {
  const [h, m] = String(hhmm || "18:00").split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return { h12, m: m || 0, ampm };
}
function from12(h12, m, ampm) {
  let h = h12 % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function fmt12(hhmm) {
  const { h12, m, ampm } = to12(hhmm);
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function ScreenshotThumb({ path, agentType }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        const res = await axios.get(`${API}/compliance-agent/screenshot`, { params: { path, agent_type: agentType }, responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch { /* ignore */ }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, agentType]);
  if (!url) return <div className="h-24 w-40 bg-gray-100 rounded animate-pulse" data-testid="screenshot-loading" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="screenshot-thumb">
      <img src={url} alt="audit screenshot" className="h-24 w-40 object-cover object-top rounded border border-gray-200 hover:border-cyan-400 transition-colors" />
    </a>
  );
}

export default function ComplianceAgent({ agentType = "vaccine_compliance" }) {
  const AGENT = AGENT_META[agentType] || AGENT_META.vaccine_compliance;
  const AgentIcon = AGENT.Icon;
  const P = { agent_type: agentType }; // axios params helper

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [sharedConn, setSharedConn] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showLiveDialog, setShowLiveDialog] = useState(false);
  const [liveConfirmText, setLiveConfirmText] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [runs, setRuns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [health, setHealth] = useState(null);
  const pollRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/settings`, { params: P });
      if (data.configured && data.settings) {
        const s = data.settings;
        setSettings(s);
        setSharedConn(null);
        setHasPassword(!!s.has_password);
        setLiveEnabled(!!s.live_enabled);
        setScheduleEnabled(!!s.schedule_enabled);
        setPaused(!!s.paused);
        setForm((f) => ({
          ...f,
          app_url: s.app_url || "", email: s.email || "", password: "",
          location_name: s.location_name || "", gingr_location_id: s.gingr_location_id || "",
          agreement_type: s.agreement_type || "Policies",
          lookahead_days: s.lookahead_days ?? 30, duplicate_window_days: s.duplicate_window_days ?? 1,
          max_sends_per_run: s.max_sends_per_run ?? 10, schedule_time: s.schedule_time || "18:00",
          timezone: s.timezone || "America/New_York", skip_row_text: s.skip_row_text || "",
        }));
      } else {
        setSettings(null);
        // Pre-fill the shared Gingr login already configured for this location
        if (data.shared_connection) {
          const c = data.shared_connection;
          setSharedConn(c);
          setHasPassword(!!c.has_password);
          setForm((f) => ({ ...f, app_url: c.app_url || "", location_name: c.location_name || "" }));
        }
      }
    } catch (e) {
      toast.error("Could not load agent settings");
    }
  }, [agentType]);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/runs`, { params: P });
      setRuns(data.runs || []);
    } catch { /* silent */ }
  }, [agentType]);

  const loadActive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/run/active`, { params: P });
      setActiveRun(data.active || null);
      return data.active;
    } catch { return null; }
  }, [agentType]);

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/health`);
      setHealth(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadRuns(), loadActive(), loadHealth()]);
      setLoading(false);
    })();
    return () => clearInterval(pollRef.current);
  }, [loadSettings, loadRuns, loadActive, loadHealth]);

  useEffect(() => {
    if (activeRun) {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const active = await loadActive();
        if (!active) {
          clearInterval(pollRef.current);
          setRunning(false);
          await loadRuns();
          toast.success("Run finished");
        }
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [activeRun, loadActive, loadRuns]);

  const saveSettings = async () => {
    // Gingr credentials are managed centrally in Agent Setup — this page only
    // saves the per-agent rules. The backend inherits the shared location login.
    if (!hasPassword) {
      toast.error("Connect this location's Gingr login in Agent Setup first");
      return;
    }
    setSaving(true);
    try {
      const { app_url, email, password, ...rest } = form; // eslint-disable-line no-unused-vars
      const payload = { ...rest, mode: "approval", enabled: true };
      await axios.post(`${API}/compliance-agent/settings`, payload, { params: P });
      toast.success("Agent settings saved");
      await loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    } finally { setSaving(false); }
  };

  const runDry = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/compliance-agent/run`, { mode: "dry-run" }, { params: P });
      toast.success("Dry-run started — sends nothing");
      setActiveRun(data.run);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start dry-run");
    }
  };

  const cancelRun = async () => {
    if (!activeRun) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/compliance-agent/run/${activeRun.id}/cancel`, {}, { params: P });
      toast.success("Run stopped — location unblocked");
      clearInterval(pollRef.current);
      setActiveRun(null);
      setRunning(false);
      await loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop the run");
    } finally { setCancelling(false); }
  };

  const armLive = async () => {
    try {
      await axios.post(`${API}/compliance-agent/settings/enable-live`, {}, { params: P });
      setLiveEnabled(true);
      toast.success("Live sending armed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not arm live"); }
  };

  const disarmLive = async () => {
    try {
      await axios.post(`${API}/compliance-agent/settings/disable-live`, {}, { params: P });
      setLiveEnabled(false); setScheduleEnabled(false);
      toast.success("Live sending disarmed");
    } catch (e) { toast.error("Could not disarm live"); }
  };

  const toggleSchedule = async (val) => {
    try {
      await axios.post(`${API}/compliance-agent/settings/schedule`, { value: val }, { params: P });
      setScheduleEnabled(val);
      toast.success(val ? "Daily auto-run enabled" : "Daily auto-run disabled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update schedule"); }
  };

  const togglePause = async (val) => {
    try {
      await axios.post(`${API}/compliance-agent/settings/pause`, { value: val }, { params: P });
      setPaused(val);
      toast.success(val ? "Agent paused" : "Agent resumed");
    } catch (e) { toast.error("Could not update pause state"); }
  };

  const runLive = async () => {
    if (liveConfirmText !== "SEND") { toast.error('Type SEND to confirm'); return; }
    setShowLiveDialog(false); setLiveConfirmText(""); setRunning(true);
    try {
      const { data } = await axios.post(`${API}/compliance-agent/run`, { mode: "live", confirm: "SEND" }, { params: P });
      toast.success(`Live run started — sends up to ${form.max_sends_per_run}`);
      setActiveRun(data.run);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start live run");
    }
  };

  const openDetail = async (runId) => {
    try {
      const { data } = await axios.get(`${API}/compliance-agent/runs/${runId}`, { params: P });
      setDetail(data);
    } catch { toast.error("Could not load run detail"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-cyan-600" /></div>;
  }

  const configured = !!settings && hasPassword;
  const primaryOf = (s) => s?.[AGENT.primaryKey] ?? s?.candidates ?? 0;

  return (
    <div className="space-y-6" data-testid="compliance-agent-page">
      {/* Back to Agents */}
      <Link to="/agents" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-cyan-700 transition-colors" data-testid="back-to-agents">
        <ArrowLeft className="h-4 w-4" /> All Agents
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <AgentIcon className="h-7 w-7 text-cyan-600" />
            {AGENT.title}
          </h1>
          <p className="text-sm text-gray-600 mt-1">{AGENT.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {liveEnabled
            ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="live-badge"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Live armed</Badge>
            : <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="approval-badge"><ShieldAlert className="h-3.5 w-3.5 mr-1" />Approval mode</Badge>}
        </div>
      </div>

      {/* Health self-check */}
      {health && !health.healthy && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="health-warning">
          <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
          <div className="text-sm text-rose-800">
            <b>Agent not ready.</b>{" "}
            {!health.encryption_key && "Encryption key (AGENT_ENCRYPTION_KEY) is missing. "}
            {!health.chromium_installed && "Browser engine is still installing or unavailable (it self-installs on first boot — retry in ~1 min). "}
            Runs will fail until this is resolved.
          </div>
        </div>
      )}

      {/* Safety banner */}
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 px-4 py-3 flex items-start gap-3" data-testid="safety-banner">
        <Lock className="h-5 w-5 text-cyan-600 mt-0.5 shrink-0" />
        <div className="text-sm text-cyan-900">
          <span className="font-semibold">Dry-run is safe.</span> A dry-run logs into Gingr, opens the {AGENT.reportName} and
          counts who <em>would</em> be actioned — it never sends anything. Live sending stays off until you explicitly arm it.
        </div>
      </div>

      {/* Latest run summary */}
      {runs.length > 0 && (() => {
        const r = runs[0];
        const s = r.summary || {};
        const diag = diagnoseRun(r);
        return (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-2" data-testid="latest-run-summary">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">Last run</span>
                <Badge variant="outline" className={r.mode === "live" ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-600"}>
                  {r.mode === "live" ? "Live" : "Dry-run"}
                </Badge>
                <span className="text-xs text-gray-500">{fmt(r.created_at)}{r.params?.lookahead_days ? ` · ${r.params.lookahead_days}-day window` : ""}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-700"><b className="text-emerald-600">{s.sent ?? 0}</b> sent</span>
                <span className="text-gray-700"><b className="text-amber-600">{s.skipped_duplicate ?? 0}</b> skipped</span>
                <span className="text-gray-700"><b className={(s.failed ?? 0) > 0 ? "text-rose-600" : "text-gray-500"}>{s.failed ?? 0}</b> failed</span>
                <span className="text-gray-500">{primaryOf(s)} candidates</span>
                <Button variant="ghost" size="sm" onClick={() => openDetail(r.id)} data-testid="latest-run-view">Review</Button>
              </div>
            </div>
            {diag && diag.tone !== "info" && (
              <button onClick={() => openDetail(r.id)}
                className={`text-left text-xs rounded-md border px-3 py-2 flex items-start gap-2 hover:brightness-95 transition ${TONE_STYLES[diag.tone]}`}
                data-testid="latest-run-diagnosis">
                <Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span><b>{diag.title}.</b> {diag.fix} <span className="underline">See details</span></span>
              </button>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings */}
        <Card className="lg:col-span-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-cyan-600" />Setup</CardTitle>
            <CardDescription>Reminder rules for this location. The Gingr login is managed in Agent Setup.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasPassword ? (
              <Link to="/agent-setup" className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800 hover:bg-emerald-100 transition-colors" data-testid="gingr-connection-link">
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0" />
                  Gingr login connected{sharedConn?.email_masked ? ` (${sharedConn.email_masked})` : ""}. Manage it in Agent Setup.</span>
                <ArrowLeft className="h-4 w-4 rotate-180 shrink-0" />
              </Link>
            ) : (
              <Link to="/agent-setup" className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 hover:bg-amber-100 transition-colors" data-testid="gingr-connection-link">
                <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />
                  No Gingr login connected for this location. Set it up in Agent Setup to enable runs.</span>
                <ArrowLeft className="h-4 w-4 rotate-180 shrink-0" />
              </Link>
            )}

            <Separator />

            {AGENT.showAgreementType && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agreement_type">Agreement type</Label>
                  <select id="agreement_type" data-testid="input-agreement-type"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    value={form.agreement_type} onChange={(e) => set("agreement_type", e.target.value)}>
                    <option value="Policies">Policies (recommended)</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Must match an Agreement Type in your Gingr “Unsigned Agreements” report.
                    Your account offers <b>Policies</b>. If it’s ever set to something Gingr
                    doesn’t have, the agent auto-selects the correct one so runs never come back empty.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="lookahead">Look-ahead (days)</Label>
                <Input id="lookahead" type="number" data-testid="input-lookahead"
                  value={form.lookahead_days} onChange={(e) => set("lookahead_days", Number(e.target.value))} />
              </div>
              <div>
                <Label htmlFor="dupwin">Dedup window (days)</Label>
                <Input id="dupwin" type="number" data-testid="input-dupwindow"
                  value={form.duplicate_window_days} onChange={(e) => set("duplicate_window_days", Number(e.target.value))} />
              </div>
              <div>
                <Label htmlFor="maxsends">Max sends / run</Label>
                <Input id="maxsends" type="number" data-testid="input-maxsends"
                  value={form.max_sends_per_run} onChange={(e) => set("max_sends_per_run", Number(e.target.value))} />
              </div>
              <div>
                <Label htmlFor="schedtime-hour">Daily run time</Label>
                <div className="flex items-center gap-1" data-testid="input-schedule-time">
                  {(() => { const t = to12(form.schedule_time); const sel = "h-10 rounded-md border border-input bg-background px-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"; return (<>
                    <select id="schedtime-hour" data-testid="schedule-hour" className={sel} value={t.h12}
                      onChange={(e) => set("schedule_time", from12(Number(e.target.value), t.m, t.ampm))}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <span className="text-gray-400">:</span>
                    <select data-testid="schedule-minute" className={sel} value={t.m}
                      onChange={(e) => set("schedule_time", from12(t.h12, Number(e.target.value), t.ampm))}>
                      {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                    </select>
                    <select data-testid="schedule-ampm" className={sel} value={t.ampm}
                      onChange={(e) => set("schedule_time", from12(t.h12, t.m, e.target.value))}>
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </>); })()}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500">Scheduled daily runs activate after you arm live sending. For now, runs are manual.</p>

            <div className="flex justify-end">
              <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn"
                className="bg-gradient-to-r from-cyan-500 to-blue-600">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Run panel */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Play className="h-5 w-5 text-cyan-600" />Run agent</CardTitle>
            <CardDescription>Manually trigger a run for this location.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!configured && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Connect Gingr in Agent Setup and save your rules to enable runs.
              </div>
            )}

            {activeRun ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-4 text-center" data-testid="active-run-panel">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
                <div className="text-sm font-semibold text-blue-800">
                  {activeRun.mode === "live" ? "Live run" : "Dry-run"} in progress…
                </div>
                <div className="text-xs text-blue-600 mt-1">Logging into Gingr & scanning the report</div>
                <Button variant="outline" size="sm" onClick={cancelRun} disabled={cancelling}
                  data-testid="cancel-run-btn"
                  className="mt-3 border-red-200 text-red-600 hover:bg-red-50">
                  {cancelling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                  Force stop this run
                </Button>
                <div className="text-[11px] text-blue-500 mt-1.5">
                  Stuck? Force stop to unblock this location.
                </div>
              </div>
            ) : (
              <Button onClick={runDry} disabled={!configured || running} data-testid="run-dry-btn"
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 h-11">
                {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />}
                Run Dry-Run (safe)
              </Button>
            )}

            <Separator />
            <div className="text-xs text-gray-500 space-y-1">
              {AGENT.steps.map((s) => (
                <div key={s} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />{s}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live sending controls */}
      <Card className="border-amber-200 bg-amber-50/30" data-testid="live-controls-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
            <ShieldCheck className="h-5 w-5 text-amber-600" />Live sending
          </CardTitle>
          <CardDescription>
            Live mode sends real {AGENT.liveNoun}. It's capped by <b>Max sends / run</b> (currently
            {" "}<b data-testid="max-sends-display">{form.max_sends_per_run}</b>) — your throttle for scaling up gradually.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-4 py-3">
            <div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                {liveEnabled ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Lock className="h-4 w-4 text-gray-400" />}
                Live sending {liveEnabled ? "armed" : "disarmed"}
              </div>
              <div className="text-xs text-gray-500">When armed, you can run live manually and (optionally) on a daily schedule.</div>
            </div>
            {liveEnabled
              ? <Button variant="outline" size="sm" onClick={disarmLive} data-testid="disarm-live-btn">Disarm</Button>
              : <Button size="sm" onClick={armLive} disabled={!configured} className="bg-amber-600 hover:bg-amber-700" data-testid="arm-live-btn">Arm live sending</Button>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div>
                <div className="font-medium text-gray-900 text-sm">Daily auto-run at {fmt12(form.schedule_time)}</div>
                <div className="text-xs text-gray-500">{form.timezone} · only when live is armed</div>
              </div>
              <Switch checked={scheduleEnabled} disabled={!liveEnabled} onCheckedChange={toggleSchedule} data-testid="schedule-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div>
                <div className="font-medium text-gray-900 text-sm">Pause agent</div>
                <div className="text-xs text-gray-500">Stops manual & scheduled runs</div>
              </div>
              <Switch checked={paused} onCheckedChange={togglePause} data-testid="pause-switch" />
            </div>
          </div>

          <Button
            onClick={() => setShowLiveDialog(true)}
            disabled={!liveEnabled || paused || running || !!activeRun}
            className="w-full bg-amber-600 hover:bg-amber-700 h-11"
            data-testid="run-live-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Run Live Now — send up to {form.max_sends_per_run}
          </Button>
        </CardContent>
      </Card>

      {/* Run history */}
      <Card className="border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-cyan-600" />Run history</CardTitle>
            <CardDescription>Recent agent runs for this location.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadRuns} data-testid="refresh-runs-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-sm text-gray-500 py-8 text-center">No runs yet. Start a dry-run above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="runs-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">Report window</th>
                    <th className="py-2 pr-4 font-medium">Mode</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Candidates</th>
                    <th className="py-2 pr-4 font-medium">Sent</th>
                    <th className="py-2 pr-4 font-medium">Skipped</th>
                    <th className="py-2 pr-4 font-medium">By</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`run-row-${r.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap">{fmt(r.created_at)}</td>
                      <td className="py-2 pr-4 whitespace-nowrap text-xs text-gray-600" data-testid={`run-window-${r.id}`}>
                        {r.params?.reservation_from
                          ? <>
                              <div>Res {r.params.reservation_from} → {r.params.reservation_to}</div>
                              <div className="text-[11px] text-gray-400">
                                {r.params.expires_before
                                  ? `Expires before ${r.params.expires_before} · ${r.params.lookahead_days}d`
                                  : `${r.params.agreement_type || "Policies"} · ${r.params.lookahead_days}d`}
                              </div>
                            </>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className={r.mode === "live" ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-600"}>
                          {r.mode === "live" ? "Live" : "Dry-run"}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLES[r.status] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                          {r.status === "completed" && <CheckCircle2 className="h-3 w-3" />}
                          {r.status === "failed" && <XCircle className="h-3 w-3" />}
                          {(r.status === "running" || r.status === "queued") && <Loader2 className="h-3 w-3 animate-spin" />}
                          {r.status}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{primaryOf(r.summary) || "—"}</td>
                      <td className="py-2 pr-4">{r.summary?.sent ?? 0}</td>
                      <td className="py-2 pr-4">{r.summary?.skipped_duplicate ?? 0}</td>
                      <td className="py-2 pr-4 text-gray-500 truncate max-w-[140px]">{r.triggered_by}</td>
                      <td className="py-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(r.id)} data-testid={`view-run-${r.id}`}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Run detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="run-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-cyan-600" />Run detail
            </DialogTitle>
            <DialogDescription>
              {detail?.run && (
                <span>{fmt(detail.run.created_at)} · {detail.run.mode} · {detail.run.status}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {detail?.run && (
            <div className="space-y-4">
              {(() => {
                const diag = diagnoseRun(detail.run, detail.actions || []);
                if (!diag) return null;
                const Icon = diag.tone === "info" ? Lightbulb : Wrench;
                return (
                  <div className={`rounded-md border px-3 py-3 ${TONE_STYLES[diag.tone]}`} data-testid="run-diagnosis">
                    <div className="flex items-start gap-2">
                      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                      <div className="space-y-1">
                        <div className="text-sm font-semibold">{diag.title}</div>
                        <div className="text-xs opacity-90"><b>Why:</b> {diag.cause}</div>
                        <div className="text-xs opacity-90"><b>How to fix:</b> {diag.fix}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {detail.run.params?.reservation_from && (
                <div className="rounded-md border border-cyan-100 bg-cyan-50/60 px-3 py-2 text-xs text-cyan-900" data-testid="detail-params">
                  <span className="font-semibold">Report window used:</span>{" "}
                  reservations <b>{detail.run.params.reservation_from} → {detail.run.params.reservation_to}</b>{" "}
                  ({detail.run.params.lookahead_days}-day look-ahead)
                  {detail.run.params.expires_before
                    ? <>, expires before <b>{detail.run.params.expires_before}</b>.</>
                    : <>, agreement type <b>{detail.run.params.agreement_type || "Policies"}</b>.</>}
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                {[
                  [AGENT.primaryLabel, primaryOf(detail.run.summary)],
                  ["Candidates", detail.run.summary?.candidates ?? 0],
                  ["Sent", detail.run.summary?.sent ?? 0],
                  ["Skipped", detail.run.summary?.skipped_duplicate ?? 0],
                  ["Failed", detail.run.summary?.failed ?? 0],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-gray-100 bg-gray-50/60 py-2">
                    <div className="text-lg font-bold text-gray-900">{v}</div>
                    <div className="text-[11px] text-gray-500">{k}</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">
                  {detail.run.mode === "live" ? AGENT.detectNounLive : AGENT.detectNounDry} ({(detail.actions || []).filter(a => (detail.run.mode === "live" ? AGENT.detectEventsLive : AGENT.detectEventsDry).includes(a.event)).length})
                </div>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {(detail.actions || []).filter(a => (detail.run.mode === "live" ? AGENT.detectEventsLive : AGENT.detectEventsDry).includes(a.event)).map((a) => (
                    <div key={a.id} className="text-xs bg-white border border-gray-100 rounded px-2.5 py-1.5 text-gray-700">
                      {a.payload?.row_text || a.payload?.name || a.payload?.label || a.event}
                    </div>
                  ))}
                  {(detail.actions || []).filter(a => a.event === AGENT.skipEvent).map((a) => (
                    <div key={a.id} className="text-xs bg-amber-50 border border-amber-100 rounded px-2.5 py-1.5 text-amber-700">
                      Skipped ({a.payload?.reason}): {a.payload?.row_text}
                    </div>
                  ))}
                </div>
              </div>

              {(() => {
                const fails = (detail.actions || []).filter(a => a.event === "send_failed");
                if (fails.length === 0) return null;
                return (
                  <div data-testid="run-failed-sends">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Couldn’t send ({fails.length}) — how to fix</div>
                    <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                      {fails.map((a) => (
                        <div key={a.id} className="text-xs bg-rose-50 border border-rose-100 rounded px-2.5 py-1.5 text-rose-800">
                          <div className="font-medium">{a.payload?.row_text || a.payload?.name || "Customer"}</div>
                          <div className="opacity-90">{explainFailure(a.payload?.reason)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const shots = (detail.run?.screenshots || []).map(s => s.storage_path).filter(Boolean);
                if (shots.length === 0) return null;
                return (
                  <div data-testid="run-screenshots">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Audit screenshots ({shots.length})</div>
                    <div className="flex gap-2 flex-wrap">
                      {shots.map((s) => <ScreenshotThumb key={s} path={s} agentType={agentType} />)}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Live run confirmation */}
      <Dialog open={showLiveDialog} onOpenChange={(o) => { if (!o) { setShowLiveDialog(false); setLiveConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="live-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <ShieldCheck className="h-5 w-5 text-amber-600" />Confirm live send
            </DialogTitle>
            <DialogDescription>{AGENT.liveBody(form.max_sends_per_run)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="live-confirm">Type <span className="font-mono font-bold">SEND</span> to confirm</Label>
            <Input id="live-confirm" data-testid="live-confirm-input" value={liveConfirmText}
              onChange={(e) => setLiveConfirmText(e.target.value)} placeholder="SEND" autoFocus />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowLiveDialog(false); setLiveConfirmText(""); }} data-testid="live-cancel-btn">Cancel</Button>
              <Button onClick={runLive} disabled={liveConfirmText !== "SEND"}
                className="bg-amber-600 hover:bg-amber-700" data-testid="live-confirm-btn">Send now</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
